import React, { Component } from "react";
import { connect } from "react-redux";
import { fetchQualificationQuestions, saveQualificationResponse } from "../actions/Qualification";

class QualificationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      questions: [],
      answers: {},
      cvFile: null,
      loading: false,
      error: ""
    };

    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadQuestions();
  }

  getLang() {
    return this.props.lang || localStorage.getItem("lang") || "fr";
  }

  getFlowType() {
    return this.props.flowType || localStorage.getItem("navigation") || "individual";
  }

  getLabel(question) {
    const lang = this.getLang();

    if (question.label) {
      return question.label;
    }

    if (lang === "en" && question.label_en) {
      return question.label_en;
    }

    if (lang === "fr" && question.label_fr) {
      return question.label_fr;
    }

    return question.label_en || question.label_fr || "";
  }

  getPlaceholder(question) {
    const lang = this.getLang();

    if (question.placeholder) {
      return question.placeholder;
    }

    if (lang === "en" && question.placeholder_en) {
      return question.placeholder_en;
    }

    if (lang === "fr" && question.placeholder_fr) {
      return question.placeholder_fr;
    }

    return question.placeholder_en || question.placeholder_fr || "";
  }

  getOptionLabel(option) {
    const lang = this.getLang();

    if (option.label) {
      return option.label;
    }

    if (lang === "en" && option.option_label_en) {
      return option.option_label_en;
    }

    if (lang === "fr" && option.option_label_fr) {
      return option.option_label_fr;
    }

    return option.option_label_en || option.option_label_fr || "";
  }

  isRequired(question) {
    return question.is_required === true || question.is_required === 1 || question.is_required === "1";
  }

  loadQuestions() {
    const params = {
      lang: this.getLang(),
      flow: this.getFlowType(),
      flow_type: this.getFlowType()
    };

    this.setState({ loading: true });

    fetchQualificationQuestions(params, response => {
      if (response && response.status === 200 && response.data.status) {
        this.setState({
          questions: response.data.data || [],
          loading: false
        });
      } else {
        this.setState({
          error: this.getLang() === "en"
            ? "Unable to load qualification questions."
            : "Impossible de charger les questions de qualification.",
          loading: false
        });
      }
    });
  }

  handleChange(question, value) {
    this.setState(prevState => ({
      answers: {
        ...prevState.answers,
        [question.question_key]: value
      },
      error: ""
    }));
  }

  handleFileChange(event) {
    this.setState({
      cvFile: event.target.files && event.target.files[0] ? event.target.files[0] : null,
      error: ""
    });
  }

  clearSelectedFile() {
    if (this.fileInputRef && this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }

    this.setState({
      cvFile: null,
      error: ""
    });
  }

  getApiErrorMessage(response) {
    const lang = this.getLang();

    const defaultMessage = lang === "en"
      ? "Unable to save qualification response."
      : "Impossible d’enregistrer la qualification.";

    if (!response || !response.data) {
      return defaultMessage;
    }

    const data = response.data;

    if (data.errors && data.errors.cv_file && data.errors.cv_file.length > 0) {
      return lang === "en"
        ? "The CV must be a PDF, DOC, or DOCX file and must not exceed 5MB."
        : "Le CV doit être au format PDF, DOC ou DOCX et ne doit pas dépasser 5 Mo.";
    }

    if (data.message === "Validation error") {
      return lang === "en"
        ? "Please check the form information and try again."
        : "Veuillez vérifier les informations du formulaire et réessayer.";
    }

    if (data.message) {
      return data.message;
    }

    return defaultMessage;
  }

  validateRequiredFields() {
    for (let i = 0; i < this.state.questions.length; i += 1) {
      const question = this.state.questions[i];

      if (!this.isRequired(question)) {
        continue;
      }

      const answer = this.state.answers[question.question_key];

      if (
        answer === undefined ||
        answer === null ||
        answer === "" ||
        (Array.isArray(answer) && answer.length === 0)
      ) {
        return this.getLabel(question);
      }
    }

    return null;
  }

  handleSubmit(event) {
    event.preventDefault();

    const missingField = this.validateRequiredFields();

    if (missingField) {
      this.setState({
        error: this.getLang() === "en"
          ? "Please complete the required fields below to get the result."
          : "Veuillez compléter les champs obligatoires ci-dessous pour obtenir le résultat."
      });
      return;
    }

    const formData = new FormData();

    formData.append("user_id", this.props.userId || localStorage.getItem("userId") || localStorage.getItem("user_id") || "");
    formData.append("client_id", this.props.clientId || localStorage.getItem("client_id") || "");
    formData.append("result_id", this.props.resultId || "");
    formData.append("test_id", this.props.testId || "");
    formData.append("test_name", this.props.testName || "");
    formData.append("flow_type", this.getFlowType());
    formData.append("language", this.getLang());

    this.state.questions.forEach(question => {
      const key = question.question_key;
      const value = this.state.answers[key];

      if (value !== undefined && value !== null && value !== "") {
        formData.append(`answers[${key}]`, value);
      }
    });

    if (this.state.cvFile) {
      formData.append("cv_file", this.state.cvFile);
    }

    this.setState({ loading: true, error: "" });

    saveQualificationResponse(formData, response => {
      if (response && response.status === 200 && response.data.status) {
        this.setState({ loading: false });

        if (this.props.onCompleted) {
          this.props.onCompleted(response.data.data);
        }
      } else {
        this.setState({
          loading: false,
          error: this.getApiErrorMessage(response)
        });
      }
    });
  }

  renderSelectField(question) {
    const value = this.state.answers[question.question_key] || "";
    const options = question.options || [];

    return (
      <select
        className="form-control qualification-input"
        value={value}
        onChange={event => this.handleChange(question, event.target.value)}
      >
        <option value="">
          {this.getLang() === "en" ? "-- Select --" : "-- Sélectionner --"}
        </option>
        {options.map(option => (
          <option key={option.id} value={option.id}>
            {this.getOptionLabel(option)}
          </option>
        ))}
      </select>
    );
  }

  renderTextAreaField(question) {
    const value = this.state.answers[question.question_key] || "";

    return (
      <textarea
        className="form-control qualification-input qualification-textarea"
        rows="4"
        placeholder={
          this.getPlaceholder(question) ||
          (this.getLang() === "en"
            ? "Tell us about your experience and what kind of job or training you are looking for..."
            : "Décrivez ici votre expérience et emploi ou formation souhaitée...")
        }
        value={value}
        onChange={event => this.handleChange(question, event.target.value)}
      />
    );
  }

  renderFileField() {
    const lang = this.getLang();

    return (
      <div className="qualification-file-box">
        <div className="qualification-file-icon">
          <i className="fa fa-file-o"></i>
        </div>
        <div className="qualification-file-input">
          <input
            ref={this.fileInputRef}
            type="file"
            className="form-control qualification-input"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={event => this.handleFileChange(event)}
          />

          {this.state.cvFile ? (
            <div className="qualification-selected-file">
              <span title={this.state.cvFile.name}>{this.state.cvFile.name}</span>
              <button
                type="button"
                className="qualification-remove-file-btn"
                onClick={() => this.clearSelectedFile()}
              >
                {lang === "en" ? "Remove" : "Supprimer"}
              </button>
            </div>
          ) : null}

          <small>
            {lang === "en"
              ? "PDF, DOC, DOCX up to 5MB"
              : "PDF, DOC, DOCX jusqu’à 5 Mo"}
          </small>
        </div>
      </div>
    );
  }

  renderQuestionField(question) {
    if (question.field_type === "textarea") {
      return this.renderTextAreaField(question);
    }

    if (question.field_type === "file") {
      return this.renderFileField(question);
    }

    /*
      Production-safe UI decision:
      Client approved 8 dropdowns. Even if backend marks some fields as multi_select,
      they are rendered as a single dropdown here to match approved UI.
    */
    return this.renderSelectField(question);
  }

  render() {
    const lang = this.getLang();
    const dropdownQuestions = this.state.questions.filter(question => {
      return question.field_type !== "textarea" && question.field_type !== "file";
    });
    const textareaQuestion = this.state.questions.find(question => question.field_type === "textarea");
    const fileQuestion = this.state.questions.find(question => question.field_type === "file");

    return (
      <div className="qualification-approved-wrapper">
        <div className="qualification-approved-card">
          <div className="qualification-card-title">
            <span></span>
            {lang === "en" ? "Please complete the form" : "Merci de compléter le formulaire"}
          </div>

          {this.state.error ? (
            <div className="qualification-form-alert">
              {this.state.error}
            </div>
          ) : null}

          <form onSubmit={event => this.handleSubmit(event)}>
            <div className="row qualification-dropdown-row">
              {dropdownQuestions.map(question => (
                <div key={question.id} className="col-lg-3 col-md-6 col-sm-12 qualification-field-col">
                  <div className="qualification-field-group">
                    <label>
                      {this.getLabel(question)}
                      {this.isRequired(question) ? <span className="qualification-required">*</span> : null}
                    </label>
                    {this.renderQuestionField(question)}
                  </div>
                </div>
              ))}
            </div>

            <div className="qualification-separator"></div>

            <div className="row qualification-extra-row">
              {textareaQuestion ? (
                <div className="col-lg-7 col-md-12 col-sm-12 qualification-field-col">
                  <div className="qualification-field-group">
                    <label>
                      {this.getLabel(textareaQuestion)}
                      {this.isRequired(textareaQuestion) ? <span className="qualification-required">*</span> : null}
                    </label>
                    {this.renderQuestionField(textareaQuestion)}
                  </div>
                </div>
              ) : null}

              {fileQuestion ? (
                <div className="col-lg-5 col-md-12 col-sm-12 qualification-field-col">
                  <div className="qualification-field-group">
                    <label>
                      {this.getLabel(fileQuestion)}
                      {!this.isRequired(fileQuestion) ? (
                        <small className="qualification-optional">
                          {lang === "en" ? " (Optional)" : " (Facultatif)"}
                        </small>
                      ) : (
                        <span className="qualification-required">*</span>
                      )}
                    </label>
                    {this.renderQuestionField(fileQuestion)}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="qualification-info-message">
              <i className="fa fa-info-circle"></i>
              <span>
                {lang === "en"
                  ? "Please complete the required fields above to get the result."
                  : "Veuillez compléter les champs obligatoires ci-dessus pour obtenir le résultat."}
              </span>
            </div>

            <div className="qualification-submit-wrap">
              <button
                type="submit"
                className="qualification-get-result-btn"
                disabled={this.state.loading}
              >
                <i className="fa fa-trophy"></i>
                {this.state.loading
                  ? (lang === "en" ? "Saving..." : "Enregistrement...")
                  : (lang === "en" ? "Get Results" : "Obtenir les résultats")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    lang: state.locale && state.locale.lang ? state.locale.lang : localStorage.getItem("lang")
  };
};

export default connect(mapStateToProps)(QualificationForm);
